(() => {
  if (!App.settings.optimizeQuickReorderAndLogin) return;

  TrackApp.reorder = (() => {
    const { userAuthenticated, userIdentified } = App.settings;
    const { isSessionStorageSupported } = App.global;
    const EVENT_CATEGORY = 'quick reorder';

    function handleUserIdentifiedInSession(value = null) {
      // User identified status should be the same in session.
      if (isSessionStorageSupported()) {
        const SESSION_NAME = 'userIdentifiedInSession';
        const fromSession = window.sessionStorage.getItem(SESSION_NAME);
        // Set session item on page load if it's empty.
        if (value !== null && fromSession === null) {
          window.sessionStorage.setItem(SESSION_NAME, value);
          return window.sessionStorage.getItem(SESSION_NAME);
        }
        return fromSession; // Get item value from sessionStorage.
      }
      return 'false';
    }

    function getUserIdentified() {
      // eslint-disable-next-line no-shadow
      const userIdentified = handleUserIdentifiedInSession();
      if (userIdentified === 'true') {
        return 'identified';
      }
      if (userIdentified === 'false') {
        return 'not identified';
      }
      return null;
    }

    function getClOrderHistory() {
      let clOrderHistory = '';
      // We know CL order history when user has been identified in our system (has quickReorderUser cookie).
      if (userIdentified || userAuthenticated) {
        const hasLensQuickReorder = App.common.quickreorder.hasLensQuickReorder();
        if (hasLensQuickReorder !== null) {
          clOrderHistory = hasLensQuickReorder ? '|CL order history' : '|no CL order history';
        }
      }
      return clOrderHistory;
    }

    function getReorderFlowType() {
      return `${getUserIdentified()}${getClOrderHistory()}`;
    }

    /**
     * Maps a product from Order model (com.lensway.common.order.Order) format to dataLayer format.
     * @param {Object} productInfo source object
     * @param {Number} [i] index of object in list
     * @return {Object}
     * @private
     */
    function mapProductData(productInfo, i) {
      const props = productInfo.product.properties.properties;
      const flowType = ` - ${getReorderFlowType()}`;

      const prod = {
        id: productInfo.product.id,
        groupId: productInfo.product.productGroupId || '',
        name: props.name.value,
        category: productInfo.product.type,
        categoryNameId: productInfo.product.categoryNameId || 'N/A',
        subCategory: (props.type && props.type.values.join(',')) || 'N/A',
        manufacturer: props.manufacturer.value || 'N/A',
        availability: TrackApp.getStockLevel(productInfo.item.availableQuantity),
        // eslint-disable-next-line max-len
        price: Math.round(((productInfo.item.listPrice.value - productInfo.item.discount.value) / window.ua.VAT) * 100) / 100,
        listName: `Quick reorder${flowType}`,
        listPosition: (i !== undefined) ? i + 1 : productInfo.listPosition,
        ribbon: JSON.stringify(productInfo.product.ribbon) || 'no ribbon',
      };

      if (productInfo.currentQuantity) {
        prod.quantity = productInfo.currentQuantity;
      }

      return prod;
    }

    function mapHomeTrialProductData(productInfo, i) {
      const stockLevel = TrackApp.getStockLevel(productInfo.item.availableQuantity);
      // eslint-disable-next-line max-len
      const calculatedPrice = Math.round(((productInfo.item.listPrice.value - productInfo.item.discount.value) / window.ua.VAT) * 100) / 100;
      const productProperties = productInfo.product.properties.properties;
      const subCategory = productProperties.type && productProperties.type.values.join(',');
      const prod = {
        id: productInfo.product.id,
        groupId: productInfo.product.productGroupId || '',
        name: productProperties.model.value,
        category: productInfo.product.type,
        categoryNameId: productInfo.product.categoryNameId || 'N/A',
        subCategory: subCategory || 'N/A',
        manufacturer: productProperties.brand.value || 'N/A',
        availability: stockLevel,
        price: calculatedPrice,
        ribbon: JSON.stringify(productInfo.product.ribbon) || 'no ribbon',
        details: {
          frame: {
            lens: {
              coating: 'Not selected',
              usage: 'Not selected',
              thickness: 'Not selected',
              type: 'Not selected',
            },
            type: productProperties.oracleRimType.value || 'N/A',
            material: productProperties.material.valuesList
            && productProperties.material.valuesList.length === 1
              ? productProperties.material.valuesList[0]
              : 'N/A',
            style: productInfo.product.style || 'N/A',
            colour: productInfo.item.color || 'N/A',
            priceGroup: Math.round(productInfo.product.productPrice.price) || 'N/A',
          },
        },
        gender: productProperties.gender.valuesList || [],
        shape: productProperties.shape.valuesList
        && productProperties.shape.valuesList.length === 1
          ? productProperties.shape.valuesList[0]
          : 'N/A',
        listName: 'Home trial ReOrder',
        listPosition: (i !== undefined) ? i + 1 : productInfo.listPosition,

      };

      if (productInfo.currentQuantity) {
        prod.quantity = productInfo.currentQuantity;
      }

      return prod;
    }

    /**
     * Tracks interactions with CTAs on quick reorder (My pages, order history, track order, shop lenses and shop glasses).
     * @param event {Object} - DOM event object
     * @param cta {String} - cta name
     */
    function trackLinkClick(event, cta) {
      TrackApp.trackInteraction(
        EVENT_CATEGORY,
        `${cta} click`,
        `${cta} click ${getReorderFlowType()}`,
      );

      if (userAuthenticated && cta !== 'track order') {
        // Track order link opens new tab - no need in manual redirect.
        setTimeout(() => {
          window.location.href = event.target.getAttribute('href');
        }, 150);
      }
    }

    function trackReorderView(quickOrderJson, autoTrigger) {
      const { HOME_TRIAL, LENS } = quickOrderJson;
      if (LENS) {
        const lensOrderItems = LENS.orderRows.map(
          (productInfo, i) => mapProductData(productInfo, i),
        );
        TrackApp.trackImpression('productListView', lensOrderItems);
        // Track quick reorder exposed for featured quick reorder CL order only.
        trackQuickReorderExposed(autoTrigger);
        return;
      }
      if (HOME_TRIAL) {
        const homeTrialItems = HOME_TRIAL.orderRows.map(
          (productInfo, i) => mapHomeTrialProductData(productInfo, i),
        );
        TrackApp.trackImpression('productListView', homeTrialItems);
      }

      TrackApp.trackInteraction(
        EVENT_CATEGORY,
        'quick reorder exposed',
        'quick reorder exposed',
        autoTrigger ? 'gaEventNonInteraction' : 'gaEvent',
      );
    }

    function trackQuickReorderExposed(autoTrigger = false) {
      TrackApp.trackInteraction(
        EVENT_CATEGORY,
        'quick reorder exposed',
        `quick reorder exposed ${getReorderFlowType()}`,
        autoTrigger ? 'gaEventNonInteraction' : 'gaEvent',
      );
    }

    function trackLoginFormExposed() {
      TrackApp.trackInteraction(
        EVENT_CATEGORY,
        'quick reorder login form exposed',
        `quick reorder login form exposed ${getReorderFlowType()}`,
      );
    }

    function trackNoReorderView() {
      trackQuickReorderExposed();
    }

    function trackReorderCTAClick(cta = '') {
      switch (cta) {
        case 'menu cta': {
          TrackApp.trackInteraction(
            EVENT_CATEGORY,
            `${cta} clicked`,
            `${cta} clicked`,
          );
          break;
        }
        default: {
          if (cta) {
            TrackApp.trackInteraction(
              EVENT_CATEGORY,
              cta,
              `${cta} clicked`,
            );
          }
        }
      }
    }

    function trackReorderProdClick($product, quickOrderJson, homeTrial) {
      const link = $product.attr('href');
      const index = homeTrial
        ? $product.closest('.home-trial__table').index()
        : $product.closest('.my-pages__table').index();
      const productInfo = homeTrial
        ? mapHomeTrialProductData(quickOrderJson.orderRows[index], index)
        : mapProductData(quickOrderJson.orderRows[index], index);
      dataLayer.push({
        event: 'productClick',
        product: productInfo,
      });
      window.location.href = App.global.getRedirectUrl(link);
    }

    function trackReorderCartAdd(quickOrdersJsonArr) {
      if (quickOrdersJsonArr.length > 1) {
        const orderItemsArr = quickOrdersJsonArr.map((row) => mapProductData(row));
        dataLayer.push({
          event: 'cartAddMultiple',
          products: orderItemsArr,
        });
      } else {
        dataLayer.push({
          event: 'cartAdd',
          product: mapProductData(quickOrdersJsonArr[0]),
        });
      }

      TrackApp.trackInteraction(
        EVENT_CATEGORY,
        'add quick reorder to cart',
        `add quick reorder to cart ${getReorderFlowType()}`,
      );
    }

    function trackOrderHistoryReorder(analyticsData) {
      if (analyticsData.productInformation.length > 1) {
        dataLayer.push({
          event: 'cartAddMultiple',
          products: TrackApp.fillProductsFields(analyticsData),
        });
      } else if (analyticsData.productInformation.length === 1) {
        dataLayer.push({
          event: 'cartAdd',
          product: TrackApp.fillProductsFields(analyticsData),
        });
      }
    }

    function trackReorderEmptyCartCTA() {
      TrackApp.trackInteraction(
        'quick reorder',
        'empty cart cta',
        'empty cart cta viewed',
        'gaEventNonInteraction',
      );
    }

    function init() {
      handleUserIdentifiedInSession(userIdentified);
    }

    return {
      init,
      trackOrderHistoryReorder,
      trackReorderCartAdd,
      trackReorderProdClick,
      trackReorderView,
      trackNoReorderView,
      trackLoginFormExposed,
      trackReorderCTAClick,
      trackLinkClick,
      getReorderFlowType,
      trackReorderEmptyCartCTA,
    };
  })();
})();
